import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideCircleHelp, lucideHouse, lucidePhone } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, WithLockableFields } from 'app/home/create-quote/form-locking';
import { LockedQuoteAlertComponent } from 'app/home/create-quote/form-locking/locked-quote-alert.component';
import { ExteriorWallQuoteFormInput, ExteriorWallValue } from 'app/home/create-quote/forms/property-details/exterior-wall/exterior-wall.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { ContactDialogService } from 'app/home/create-quote/services/contact-dialog/contact-dialog.service';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import * as cc from 'app/shared/constants/contact-constants';
import { PhonePipe } from 'app/shared/pipes/phone-number/phone.pipe';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-exterior-wall',
  imports: [FormCardComponent, NgIconComponent, ReactiveFormsModule, PhonePipe, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  templateUrl: './exterior-wall.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideIcons({ lucideCircleAlert, lucideCircleHelp, lucideHouse, lucidePhone })],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/property-details/exterior-wall',
  isAutoAdvanced: true,
})
export class QuoteFormExteriorWallComponent extends BaseQuoteForm<ExteriorWallQuoteFormInput> implements OnInit, Lockable {
  private optionsStore = inject(QuoteFormOptionsStore);
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;
  protected contactDialogService = inject(ContactDialogService);
  public formDataIn: ExteriorWallQuoteFormInput = this.quoteDraftStore.exteriorWallQuoteFormSelector();

  public exteriorWallOptions = this.optionsStore.exteriorWallQuoteFormOptionsSelector();

  public formDefinition = {
    exteriorWall: new FormControl<ExteriorWallValue | undefined>(undefined, Validators.required),
  };
  public exteriorWallForm = new FormGroup(this.formDefinition);

  public salesPhoneNumber = cc.PHONE_NUMBERS.sales;
  public salesPhoneHoursFormatted = cc.PHONE_HOURS_FORMATTED.sales;

  public override autoAdvanceControlRef = this.exteriorWallForm.controls.exteriorWall;

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('exteriorWall')) {
      this.exteriorWallForm.controls.exteriorWall.setValue(this.formDataIn.exteriorWall);
    }
  }

  public getFormControls() {
    return this.exteriorWallForm.controls;
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  private getFormValues(): Pick<QuoteDraftState, 'exteriorWall'> {
    const { controls } = this.exteriorWallForm;
    const formValues = {
      exteriorWall: controls.exteriorWall.value ?? undefined,
    };

    return formValues;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.exteriorWallForm.markAllAsTouched();
    if (this.exteriorWallForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public get showError() {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.exteriorWallForm.get('exteriorWall');
    return !!control?.invalid && !!control?.touched;
  }
}
